<template>
  <div>
    <b-row
      class="gutters-0 align-items-end new-gutters-sm"
      :key="periodKey"
    >
      <b-col
        cols="6"
      >
        <campaign-period
          :periods="periods"
          :period-index="periodIndex"
          :period-date-range="periodDateRange"
          :is-period-running="isPeriodRunning"
          :has-period-ended="hasPeriodEnded"
          @updateDates="updateDates"
        />
      </b-col>
      <b-col
        lg="4"
        xl="3"
      >
        <period-budget
          :period-index="periodIndex"
          :initial-budget="budgetValue"
          :has-period-ended="hasPeriodEnded"
          @updateBudget="updateBudget"
        />
      </b-col>
      <b-col
        lg="2"
      >
        <button
          v-if="periods.length > 1"
          class="btn btn-danger"
          @click="deletePeriod"
          :disabled="isDeletePeriodDisabled"
        >
          <i class="fa-light fa-trash-can" />
        </button>
      </b-col>
    </b-row>
    <b-row
      v-if="!GET_IS_CAMPAIGN_ENDED"
      class="gutters-0"
    >
      <p
        v-if="shouldDisplayValidationMessages"
        class="mb-0 alert alert-sm alert-danger mt-3"
      >
        <template v-if="isDateRangeBelowMin && isBudgetBelowMin">
          The campaign period must span over at least 3 days and the minimum budget is {{ GET_SELECTED_CURRENCY.symbolString + budgetLimit.min }}
        </template>
        <span
          v-else-if="!isValidDateRange"
          :data-test="`campaign-period-${periodIndex}-date-range-alert`"
        >
          <template v-if="isDateRangeBelowMin">
            The campaign period must span over at least 3 days
          </template>
        </span>
        <span
          v-else-if="!isValidBudget"
          :data-test="`campaign-period-${periodIndex}-budget-spend-alert`"
        >
          <template v-if="isBudgetBelowMin">
            The minimum budget is {{ GET_SELECTED_CURRENCY.symbolString + budgetLimit.min }}
          </template>
          <template v-else-if="isBudgetOverMax">
            The maximum allowed budget is {{ GET_SELECTED_CURRENCY.symbolString + budgetLimit.max }}
          </template>
          <template v-else-if="isNewBudgetBelowSpentBudget">
            The campaign has already spent more than the budget you have entered.
          </template>
        </span>
        <template v-else-if="isBudgetBelowSpendThreshold">
          Budget is below minimum spend for period
        </template>
      </p>
      <p
        v-else-if="GET_IS_SELF_SERVICE_CAMPAIGN"
        class="alert alert-sm alert-info mt-3"
      >
        We will use your budget of {{ GET_SELECTED_CURRENCY.symbolString + GET_CAMPAIGN_TOTAL_BUDGET }} to buy inventory on a range of premium publishers.
      </p>
    </b-row>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';

import CampaignPeriod from '@/modules/campaigns/components/CampaignInfo/CampaignPeriod';
import PeriodBudget from '@/modules/campaigns/components/CampaignInfo/PeriodBudget';

import dateHelper from '@/core/mixins/dateHelper';

import {
  parseISO, isPast, isFuture, isValid, differenceInDays,
} from 'date-fns';

export default {
  name: 'CampaignPeriodControls',
  mixins: [
    dateHelper,
  ],
  components: {
    CampaignPeriod,
    PeriodBudget,
  },
  props: {
    periodIndex: {
      type: Number,
      required: true,
      default: 0,
    },
    period: {
      type: Object,
      required: true,
      default: () => ({
        budget: null,
        startDate: null,
        endDate: null,
      }),
    },
    periods: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      newPeriod: {
        budget: null,
        startDate: null,
        endDate: null,
      },
      periodKey: 0,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'order',
      'campaignStats',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN,
      campaignTypes.GET_SELECTED_CURRENCY,
      campaignTypes.GET_CAMPAIGN_TOTAL_BUDGET,
      campaignTypes.GET_IS_CAMPAIGN_DRAFT,
      campaignTypes.GET_IS_CAMPAIGN_REVIEW,
      campaignTypes.GET_IS_CAMPAIGN_ENDED,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
    ]),
    periodDateRange() {
      return {
        startDate: this.newPeriod.startDate,
        endDate: this.newPeriod.endDate,
      };
    },
    budgetValue() {
      return this.newPeriod.budget ?? this.period.budget;
    },
    budgetLimit() {
      return {
        min: this.GET_IS_MANAGED_CAMPAIGN ? 0 : 300,
        max: this.GET_IS_MANAGED_CAMPAIGN ? Number.MAX_VALUE : 300000,
      };
    },
    isBudgetBelowMin() {
      return !!this.budgetValue && this.budgetValue < this.budgetLimit.min;
    },
    isBudgetOverMax() {
      return !!this.budgetValue && this.budgetValue > this.budgetLimit.max;
    },
    ISOStartDate() {
      if (!this.newPeriod.startDate) return null;
      return parseISO(this.newPeriod.startDate);
    },
    ISOEndDate() {
      if (!this.newPeriod.endDate) return null;
      return parseISO(this.newPeriod.endDate);
    },
    isBudgetBelowSpendThreshold() {
      if (!this.budgetValue || !this.isValidDateRange) return false;
      const numberOfDays = differenceInDays(this.ISOEndDate, this.ISOStartDate) + 1;
      return (this.budgetValue / 100) < numberOfDays;
    },
    isNewBudgetBelowSpentBudget() {
      if (!this.isPeriodRunning) return false;
      let periodsSum = 0;
      for (let index = 0; index <= this.periodIndex; index++) {
        periodsSum += this.periods[index].budget;
      }
      const budgetSpentPercentage = 100 - this.campaignStats.budgetRemaining;
      const alreadySpentBudget = (this.getOriginalCampaignTotalBudget() * budgetSpentPercentage) / 100;
      if (periodsSum <= alreadySpentBudget) return true;
      return false;
    },
    isValidBudget() {
      return !!this.budgetValue
      && !this.isBudgetBelowMin
      && !this.isBudgetOverMax
      && !this.isNewBudgetBelowSpentBudget;
    },
    isDateRangeBelowMin() {
      const numberOfDays = differenceInDays(this.ISOEndDate, this.ISOStartDate) + 1;
      return numberOfDays < 3;
    },
    isValidDateRange() {
      return isValid(this.ISOStartDate)
      && isValid(this.ISOEndDate)
      && !this.isDateRangeBelowMin;
    },
    isValidPeriod() {
      return this.isValidBudget
      && this.isValidDateRange
      && !this.isBudgetBelowSpendThreshold;
    },
    isPeriodRunning() {
      return !this.GET_IS_CAMPAIGN_DRAFT
      && isPast(new Date(this.newPeriod.startDate))
      && isFuture(new Date(this.newPeriod.endDate));
    },
    hasPeriodEnded() {
      if (!this.newPeriod.startDate || !this.newPeriod.endDate) return false;
      return isPast(new Date(this.newPeriod.startDate)) && isPast(new Date(this.newPeriod.endDate));
    },
    isDeletePeriodDisabled() {
      if (this.GET_IS_CAMPAIGN_DRAFT) return false;
      return this.isPeriodRunning
      || this.hasPeriodEnded
      || this.GET_IS_CAMPAIGN_REVIEW;
    },
    shouldDisplayValidationMessages() {
      return (this.budgetValue && !this.isValidBudget)
      || (this.newPeriod.startDate && this.newPeriod.endDate && !this.isValidDateRange)
      || this.isBudgetBelowSpendThreshold;
    },
  },
  methods: {
    getOriginalCampaignTotalBudget() {
      return this.order.budget ?? 0;
    },
    updateBudget(budget) {
      this.newPeriod.budget = budget;
      this.emitNewPeriod();
    },
    updateDates(startDate, endDate) {
      this.newPeriod.startDate = this.convertDateToString(startDate);
      this.newPeriod.endDate = this.convertDateToString(endDate);
      this.emitNewPeriod();
    },
    emitNewPeriod() {
      this.$emit('updatePeriodsValidation', this.isValidPeriod);
      this.$emit('updatePeriod', {
        budget: this.budgetValue,
        startDate: this.newPeriod.startDate,
        endDate: this.newPeriod.endDate,
      });
    },
    deletePeriod() {
      if (this.isDeletePeriodDisabled) return;
      this.newPeriod = {
        budget: null,
        startDate: null,
        endDate: null,
      };
      this.$emit('deletePeriod');
      this.periodKey++;
    },
  },
  mounted() {
    this.newPeriod.startDate = this.period.startDate;
    this.newPeriod.endDate = this.period.endDate;
  },
};
</script>
